import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';

/*
USAGE:

<ProductTreeSelector
    onSelectionChange={(selectedIds) => {
        console.log(selectedIds);
    }}
/>

API RESPONSE FORMAT:

{
    Families: [
        { Id: 1, Name: "fam1" },
    ],
    Products: [
        { Id: 100, Name: "Prod1", FamilyId: 1 },
    ],
}
*/


const ProductTreeSelector = ({ onSelectionChange }) => {
    const [families, setFamilies] = useState([]);
    const [products, setProducts] = useState([]);

    const [selectedProducts, setSelectedProducts] = useState([]);

    const [productsExpanded, setProductsExpanded] = useState(false);

    const [expandedFamilies, setExpandedFamilies] = useState({});

    const productsCheckboxRef = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedProducts);
        }
    }, [selectedProducts]);

    const loadData = async () => {
        try {
            const response = await fetch(
                "/umbraco/api/RIMSRegistrations/FamiliesAndProducts/"
            );

            const data = await response.json();

            setFamilies(data.Families || []);
            setProducts(data.Products || []);
        } catch (err) {
            console.error("Failed loading products tree", err);
        }
    };

    const groupedFamilies = useMemo(() => {
        const mappedFamilies = families.map((family) => {
            const familyProducts = products
                .filter((product) => product.FamilyId === family.Id)
                .sort((a, b) => a.Name.localeCompare(b.Name));

            return {
                ...family,
                Products: familyProducts,
            };
        });

        const orphanProducts = products
            .filter(
                (p) =>
                    !p.FamilyId ||
                    !families.some((f) => f.Id === p.FamilyId)
            )
            .sort((a, b) => a.Name.localeCompare(b.Name));

        if (orphanProducts.length > 0) {
            mappedFamilies.push({
                Id: "__no_family__",
                Name: "- Products without family -",
                Products: orphanProducts,
            });
        }

        // SORT families by name
        return mappedFamilies.sort((a, b) =>
            a.Name.localeCompare(b.Name)
        );
    }, [families, products]);

    // -----------------------------
    // Selection Helpers
    // -----------------------------

    const isProductSelected = (productId) => {
        return selectedProducts.includes(productId);
    };

    const isFamilyFullySelected = (family) => {
        if (!family.Products.length) return false;

        return family.Products.every((p) =>
            selectedProducts.includes(p.Id)
        );
    };

    const isFamilyPartiallySelected = (family) => {
        const selectedCount = family.Products.filter((p) =>
            selectedProducts.includes(p.Id)
        ).length;

        return (
            selectedCount > 0 &&
            selectedCount < family.Products.length
        );
    };

    const areAllProductsSelected = () => {
        if (!products.length) return false;

        return products.every((p) =>
            selectedProducts.includes(p.Id)
        );
    };

    const areSomeProductsSelected = () => {
        return selectedProducts.length > 0;
    };

    // -----------------------------
    // Product Selection
    // -----------------------------

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) => {
            if (prev.includes(productId)) {
                return prev.filter((id) => id !== productId);
            }
            return [...prev, productId];
        });
    };

    // -----------------------------
    // Family Selection
    // -----------------------------

    const toggleFamily = (family) => {
        const familyProductIds = family.Products.map((p) => p.Id);

        const fullySelected = familyProductIds.every((id) =>
            selectedProducts.includes(id)
        );

        if (fullySelected) {
            setSelectedProducts((prev) =>
                prev.filter((id) => !familyProductIds.includes(id))
            );
        } else {
            setSelectedProducts((prev) => [
                ...new Set([...prev, ...familyProductIds]),
            ]);
        }
    };

    // -----------------------------
    // Global Selection
    // -----------------------------

    const toggleAllProducts = () => {
        if (areAllProductsSelected()) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(products.map((p) => p.Id));
        }
    };

    // -----------------------------
    // Indeterminate Checkbox States
    // -----------------------------

    useEffect(() => {
        if (productsCheckboxRef.current) {
            productsCheckboxRef.current.indeterminate =
                areSomeProductsSelected() &&
                !areAllProductsSelected();
        }
    }, [selectedProducts]);

    // -----------------------------
    // Expand / Collapse
    // -----------------------------

    const toggleFamilyExpand = (familyId) => {
        setExpandedFamilies((prev) => ({
            ...prev,
            [familyId]: !prev[familyId],
        }));
    };

    return (
        <div style={{ width: "100%" }}>
            {/* ROOT */}
            <div
                style={{
                    display: "flex",
                    alignItems: "start",
                    gap: "8px",
                    fontWeight: "600",
                }}
            >
                <span
                    style={{
                        cursor: "pointer",
                        minWidth: "16px",
                        display: "inline-block",
                    }}
                    onClick={() =>
                        setProductsExpanded(!productsExpanded)
                    }
                >
                    {productsExpanded ? "▼" : "▶"}
                </span>

                <input
                    ref={productsCheckboxRef}
                    type="checkbox"
                    style={{marginTop: "4px"}}
                    checked={areAllProductsSelected()}
                    onChange={toggleAllProducts}
                />

                <span>📦</span>
                <span>Products</span>
            </div>

            {/* FAMILIES */}
            {productsExpanded && (
                <div style={{ marginLeft: "24px", marginTop: "8px" }}>
                    {groupedFamilies.map((family) => {
                        const familyCheckboxRef =
                            React.createRef();

                        setTimeout(() => {
                            if (familyCheckboxRef.current) {
                                familyCheckboxRef.current.indeterminate =
                                    isFamilyPartiallySelected(family);
                            }
                        }, 0);

                        return (
                            <div
                                key={family.Id}
                                style={{ marginBottom: "8px" }}
                            >
                                {/* FAMILY ROW */}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "start",
                                        gap: "8px",
                                    }}
                                >
                                    <span
                                        style={{
                                            cursor: "pointer",
                                            minWidth: "16px",
                                            display: "inline-block",
                                        }}
                                        onClick={() =>
                                            toggleFamilyExpand(family.Id)
                                        }
                                    >
                                        {expandedFamilies[family.Id]
                                            ? "▼"
                                            : "▶"}
                                    </span>

                                    <input
                                        ref={familyCheckboxRef}
                                        type="checkbox"
                                        style={{marginTop: "4px"}}
                                        checked={isFamilyFullySelected(
                                            family
                                        )}
                                        onChange={() =>
                                            toggleFamily(family)
                                        }
                                    />

                                    <span>📁</span>
                                    <span>{family.Name}</span>
                                </div>

                                {/* PRODUCTS */}
                                {expandedFamilies[family.Id] && (
                                    <div
                                        style={{
                                            marginLeft: "32px",
                                            marginTop: "6px",
                                        }}
                                    >
                                        {family.Products.map(
                                            (product) => (
                                                <div
                                                    key={product.Id}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "start",
                                                        gap: "8px",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            minWidth: "16px",
                                                        }}
                                                    />

                                                    <input
                                                        type="checkbox"
                                                        style={{marginTop: "4px"}}
                                                        checked={isProductSelected(
                                                            product.Id
                                                        )}
                                                        onChange={() =>
                                                            toggleProduct(
                                                                product.Id
                                                            )
                                                        }
                                                    />

                                                    <span></span>
                                                    <span>
                                                        {product.SKUCode}&nbsp;&nbsp;•&nbsp;&nbsp;{product.Name}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProductTreeSelector;